import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLists } from '../contexts/ListsContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useReorderMode } from '../contexts/ReorderModeContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { useCollections } from '../contexts/CollectionsContext';
import { ListGridSkeleton } from '../components/Skeleton';
import CollaboratorsModal from '../components/CollaboratorsModal';
import ContextualTooltip from '../components/ContextualTooltip';
import CreateCollectionModal from '../components/Collections/CreateCollectionModal';
import EditCollectionModal from '../components/Collections/EditCollectionModal';
import {
  PinnedDropZone,
  UnpinnedListsSection,
  CollectionsDropZone,
  CollectionListsDropZone,
  ProfileDrawer,
  UserListModal,
  ArchivedListsSection,
  CollaborationsSection,
  MyListsHeader,
} from '../components/MyLists';
import { useListsWithComputed, useSummaryStats, useFilteredLists } from '../hooks/useListsWithComputed';
import api from '../services/api';
import '../styles/MyLists.css';

// Session cache for followers/friends modals (5 minute TTL)
const CACHE_TTL = 5 * 60 * 1000;
const myListsCache = {
  followers: { data: null, timestamp: 0 },
  friends: { data: null, timestamp: 0 },
};

const isCacheValid = (cacheEntry) => {
  return cacheEntry.data !== null && (Date.now() - cacheEntry.timestamp) < CACHE_TTL;
};

function MyLists() {
  const { lists, collaborationLists, archivedLists, loading, reorderLists, unarchiveList } = useLists();
  const { user } = useAuth();
  const { canAccess, getLimit } = useSubscription();
  const { showAlert, showToast } = useNotificationModal();
  const { notifyAction } = useTutorial();
  const { isReorderMode, toggleReorderMode, disableReorderMode } = useReorderMode();
  const { visitedPlaceIds, getDesignation, ensureDesignationsLoaded } = useUserDesignations();
  const { collections, loading: collectionsLoading, reorderCollections, updateCollection } = useCollections();
  const navigate = useNavigate();
  const location = useLocation();

  // UI State
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [friendsModalOpen, setFriendsModalOpen] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [collaboratorsModalOpen, setCollaboratorsModalOpen] = useState(false);
  const [selectedCollabListId, setSelectedCollabListId] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [restoringListId, setRestoringListId] = useState(null);
  const [isCompactView, setIsCompactView] = useState(() => {
    return localStorage.getItem('myListsCompactView') === 'true';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  // Tab state
  const canAccessCollections = canAccess('collections');
  const [activeTab, setActiveTab] = useState(() => {
    if (!canAccessCollections) return 'lists';
    const saved = localStorage.getItem('myListsActiveTab');
    return saved === 'collections' ? 'collections' : 'lists';
  });

  // Collection modals
  const [createCollectionModalOpen, setCreateCollectionModalOpen] = useState(false);
  const [editCollectionModalOpen, setEditCollectionModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [expandedCollectionId, setExpandedCollectionId] = useState(null);

  // Refs
  const searchInputRef = useRef(null);
  const reorderBtnRef = useRef(null);
  const headerRef = useRef(null);

  // Premium feature flags
  const canReorderLists = canAccess('reorderLists');
  const maxPinnedLists = getLimit('maxPinnedLists');

  // Get the user ID
  const getUserId = () => user?.id || user?._id || user?.uid || user?.userId;

  // Computed lists using extracted hook
  const { sortedLists, pinnedLists, unpinnedLists, sortedCollaborationLists } = useListsWithComputed(
    lists,
    collaborationLists,
    visitedPlaceIds
  );

  // Summary stats using extracted hook
  const summaryStats = useSummaryStats(lists, collaborationLists, getDesignation);

  // Count pinned lists
  const pinnedCount = useMemo(() => lists.filter(list => list.pinned).length, [lists]);

  // Filtered lists using extracted hook
  const filteredLists = useFilteredLists(sortedLists, searchQuery);
  const filteredPinnedLists = useFilteredLists(pinnedLists, searchQuery);
  const filteredUnpinnedLists = useFilteredLists(unpinnedLists, searchQuery);
  const filteredCollaborationLists = useFilteredLists(sortedCollaborationLists, searchQuery);

  // Filter collections based on search query
  const getListsForCollection = useCallback((listIds) => {
    if (!listIds || listIds.length === 0) return [];
    const allLists = [...sortedLists, ...sortedCollaborationLists];
    return listIds
      .map(id => allLists.find(l => l.id === id))
      .filter(Boolean);
  }, [sortedLists, sortedCollaborationLists]);

  const filteredCollections = useMemo(() => {
    if (!searchQuery.trim()) return collections;

    const query = searchQuery.toLowerCase().trim();
    return collections.filter(collection => {
      const title = (collection.title || '').toLowerCase();
      if (title.includes(query)) return true;

      const collectionLists = getListsForCollection(collection.listIds);
      const matchesList = collectionLists.some(list => {
        const listTitle = (list.title || list.name || '').toLowerCase();
        return listTitle.includes(query);
      });
      return matchesList;
    });
  }, [collections, searchQuery, getListsForCollection]);

  // Handle redirect from Premium page to settings
  useEffect(() => {
    const openSettingsValue = location.state?.openSettings;
    if (openSettingsValue) {
      const section = openSettingsValue === true ? null : openSettingsValue;
      navigate('/settings' + (section ? `?section=${section}` : ''), { replace: true });
    }
  }, [location.state, navigate]);

  // Ensure user designations are loaded (for displaying % visited stats)
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  // Scroll detection for sticky header border
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch followers when modal opens (with caching)
  useEffect(() => {
    const userId = getUserId();
    if (followersModalOpen && userId) {
      if (isCacheValid(myListsCache.followers)) {
        setFollowers(myListsCache.followers.data);
        return;
      }

      setLoadingFollowers(true);
      api.getFollowers(userId)
        .then(response => {
          const followersData = Array.isArray(response) ? response : (response.data || []);
          setFollowers(followersData);
          myListsCache.followers = { data: followersData, timestamp: Date.now() };
        })
        .catch(err => {
          console.error('Failed to load followers:', err);
          setFollowers([]);
        })
        .finally(() => setLoadingFollowers(false));
    }
  }, [followersModalOpen, user]);

  // Fetch friends when modal opens (with caching)
  useEffect(() => {
    const userId = getUserId();
    if (friendsModalOpen && userId) {
      if (isCacheValid(myListsCache.friends)) {
        setFriends(myListsCache.friends.data);
        return;
      }

      setLoadingFriends(true);
      api.getFriends(userId)
        .then(response => {
          const friendsData = Array.isArray(response) ? response : (response.data || []);
          setFriends(friendsData);
          myListsCache.friends = { data: friendsData, timestamp: Date.now() };
        })
        .catch(err => {
          console.error('Failed to load friends:', err);
          setFriends([]);
        })
        .finally(() => setLoadingFriends(false));
    }
  }, [friendsModalOpen, user]);

  // Disable reorder mode when unmounting
  useEffect(() => {
    return () => {
      disableReorderMode();
    };
  }, [disableReorderMode]);

  // Close add menu when tutorial ends
  useEffect(() => {
    const handleTutorialEnded = () => {
      setShowAddMenu(false);
    };
    window.addEventListener('tutorialEnded', handleTutorialEnded);
    return () => window.removeEventListener('tutorialEnded', handleTutorialEnded);
  }, []);

  // Handlers
  const handleUserClick = useCallback((userData) => {
    setFollowersModalOpen(false);
    setFriendsModalOpen(false);
    setIsProfileDrawerOpen(false);
    navigate(`/user/${userData.username || userData.id}`);
  }, [navigate]);

  const handleListClick = useCallback((listId) => {
    navigate(`/list/${listId}`);
  }, [navigate]);

  const handleCollaboratorsClick = useCallback((e, listId) => {
    e.stopPropagation();
    setSelectedCollabListId(listId);
    setCollaboratorsModalOpen(true);
  }, []);

  const handleRestoreList = async (e, listId) => {
    e.stopPropagation();
    setRestoringListId(listId);
    try {
      await unarchiveList(listId);
      showToast('List restored successfully');
    } catch (error) {
      console.error('Failed to restore list:', error);
      showAlert(error.message || 'Failed to restore list', { variant: 'error' });
    } finally {
      setRestoringListId(null);
    }
  };

  const handleToggleCompactView = () => {
    const newValue = !isCompactView;
    setIsCompactView(newValue);
    localStorage.setItem('myListsCompactView', String(newValue));
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    localStorage.setItem('myListsActiveTab', tab);
    setSearchQuery('');
    if (isReorderMode) {
      disableReorderMode();
    }
  };

  const handleToggleAddMenu = (value) => {
    if (!isReorderMode) {
      setShowAddMenu(value);
      if (value) {
        notifyAction('menuOpened');
      }
    }
  };

  const handleNewList = () => {
    setShowAddMenu(false);
    navigate('/lists/new');
  };

  const handleAddPlace = () => {
    setShowAddMenu(false);
    navigate('/place/add');
  };

  const handleNewCollection = () => {
    setShowAddMenu(false);
    setCreateCollectionModalOpen(true);
  };

  const handleCollectionClick = (collection) => {
    if (isReorderMode) return;
    setExpandedCollectionId(prev => prev === collection.id ? null : collection.id);
  };

  const handleCollectionEdit = (collection) => {
    setSelectedCollection(collection);
    setEditCollectionModalOpen(true);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  // Drag-and-drop handlers
  const handleListDrop = async (listId, fromZone, toZone, dropIndex) => {
    const list = sortedLists.find(l => l.id === listId);
    if (!list) return;

    const wasPinned = fromZone === 'pinned';
    const willBePinned = toZone === 'pinned';

    if (willBePinned && !wasPinned) {
      if (pinnedCount >= maxPinnedLists) {
        showToast(`You can only pin up to ${maxPinnedLists} lists.`, { variant: 'warning' });
        return;
      }
    }

    try {
      const targetLists = toZone === 'pinned'
        ? sortedLists.filter(l => l.pinned)
        : sortedLists.filter(l => !l.pinned);

      const filteredTargetLists = targetLists.filter(l => l.id !== listId);
      filteredTargetLists.splice(dropIndex, 0, list);

      // Build batch updates array
      const updatesArray = [];
      for (let i = 0; i < filteredTargetLists.length; i++) {
        const l = filteredTargetLists[i];
        if (l.id === listId) {
          updatesArray.push({
            listId,
            updates: {
              pinned: willBePinned,
              position: i,
              ...(willBePinned ? { pinnedAt: new Date() } : { pinnedAt: undefined })
            }
          });
        } else if (l.position !== i) {
          updatesArray.push({ listId: l.id, updates: { position: i } });
        }
      }

      // Handle unpinning: reorder remaining pinned lists
      if (wasPinned && !willBePinned) {
        const remainingPinned = sortedLists.filter(l => l.pinned && l.id !== listId);
        for (let i = 0; i < remainingPinned.length; i++) {
          if (remainingPinned[i].position !== i) {
            updatesArray.push({ listId: remainingPinned[i].id, updates: { position: i } });
          }
        }
      }

      // Use batch reorder for instant UI update
      await reorderLists(updatesArray);
    } catch (error) {
      console.error('Failed to reorder list:', error);
      showToast('Failed to reorder list', { variant: 'error' });
    }
  };

  const handleCollectionDrop = async (collectionId, dropIndex) => {
    const collection = filteredCollections.find(c => c.id === collectionId);
    if (!collection) return;

    try {
      const currentIndex = filteredCollections.findIndex(c => c.id === collectionId);
      if (currentIndex === dropIndex) return;

      const newOrder = filteredCollections.map(c => c.id);
      newOrder.splice(currentIndex, 1);
      newOrder.splice(dropIndex, 0, collectionId);

      await reorderCollections(newOrder);
    } catch (error) {
      console.error('Failed to reorder collection:', error);
      showToast('Failed to reorder collection', { variant: 'error' });
    }
  };

  const handleCollectionListDrop = async (collectionId, listId, dropIndex) => {
    const collection = collections.find(c => c.id === collectionId);
    if (!collection || !collection.listIds) {
      return;
    }

    const currentIndex = collection.listIds.indexOf(listId);
    if (currentIndex === -1 || currentIndex === dropIndex) {
      return;
    }

    try {
      const newListIds = [...collection.listIds];
      newListIds.splice(currentIndex, 1);
      newListIds.splice(dropIndex, 0, listId);

      await updateCollection(collectionId, { listIds: newListIds });
    } catch (error) {
      console.error('Failed to reorder list in collection:', error);
      showToast('Failed to reorder list in collection', { variant: 'error' });
    }
  };

  return (
    <div className="my-lists-container">
      <div className="content-wrapper">
        <MyListsHeader
          ref={headerRef}
          user={user}
          isScrolled={isScrolled}
          isReorderMode={isReorderMode}
          isCompactView={isCompactView}
          showAddMenu={showAddMenu}
          canReorderLists={canReorderLists}
          listCount={lists.length}
          canAccessCollections={canAccessCollections}
          onProfileClick={() => setIsProfileDrawerOpen(true)}
          onToggleCompactView={handleToggleCompactView}
          onToggleReorderMode={toggleReorderMode}
          onToggleAddMenu={handleToggleAddMenu}
          onNewList={handleNewList}
          onNewCollection={handleNewCollection}
          onAddPlace={handleAddPlace}
          reorderBtnRef={reorderBtnRef}
        />

        {/* Tabs - only show if premium user */}
        {canAccessCollections && (
          <div className="mylists-tabs">
            <button
              className={`mylists-tab ${activeTab === 'lists' ? 'active' : ''}`}
              onClick={() => handleTabChange('lists')}
            >
              Lists
            </button>
            <button
              className={`mylists-tab ${activeTab === 'collections' ? 'active' : ''}`}
              onClick={() => handleTabChange('collections')}
            >
              Collections
            </button>
          </div>
        )}

        {/* Search Bar */}
        {((activeTab === 'lists' && (lists.length > 0 || collaborationLists.length > 0)) ||
          (activeTab === 'collections' && collections.length > 0)) && !isReorderMode && (
          <div className="lists-search-container">
            <div className="lists-search-bar">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className="lists-search-input"
                placeholder={activeTab === 'collections' ? 'Search collections...' : 'Search lists, locations, places...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={activeTab === 'collections' ? 'Search collections' : 'Search lists'}
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                {activeTab === 'collections'
                  ? `${filteredCollections.length} ${filteredCollections.length === 1 ? 'result' : 'results'}`
                  : `${filteredLists.length + filteredCollaborationLists.length} ${filteredLists.length + filteredCollaborationLists.length === 1 ? 'result' : 'results'}`
                }
              </div>
            )}
          </div>
        )}

        {/* Lists Tab Content */}
        {activeTab === 'lists' && (
          <>
            {loading ? (
              <ListGridSkeleton count={3} />
            ) : (
              <div className={`lists-container ${isReorderMode ? 'reorder-mode' : ''} ${isCompactView ? 'compact-view' : ''}`}>
                {/* Pinned Lists Section */}
                {filteredPinnedLists.length > 0 && canReorderLists && (
                  <PinnedDropZone
                    pinnedLists={filteredPinnedLists}
                    isCompactView={isCompactView}
                    isReorderMode={isReorderMode}
                    onDrop={handleListDrop}
                    onListClick={handleListClick}
                    maxPinnedLists={maxPinnedLists}
                  />
                )}

                {/* Unpinned Lists Section */}
                <UnpinnedListsSection
                  unpinnedLists={filteredUnpinnedLists}
                  isCompactView={isCompactView}
                  isReorderMode={isReorderMode}
                  onDrop={handleListDrop}
                  onListClick={handleListClick}
                  hasPinnedLists={filteredPinnedLists.length > 0}
                  maxPinnedLists={maxPinnedLists}
                  pinnedCount={pinnedCount}
                />
              </div>
            )}

            {!loading && lists.length === 0 && collaborationLists.length === 0 && (
              <div className="empty-state">
                <p>You haven't created any lists yet.</p>
                <button className="btn-primary" onClick={() => navigate('/lists/new')}>Create your first list</button>
              </div>
            )}

            {/* Collaborations Section */}
            {!loading && (
              <CollaborationsSection
                collaborationLists={filteredCollaborationLists}
                isCompactView={isCompactView}
                isReorderMode={isReorderMode}
                hasArchivedLists={archivedLists.length > 0}
                onListClick={handleListClick}
                onCollaboratorsClick={handleCollaboratorsClick}
              />
            )}

            {/* Archived Section */}
            {!loading && (
              <ArchivedListsSection
                archivedLists={archivedLists}
                visitedPlaceIds={visitedPlaceIds}
                onListClick={handleListClick}
                onRestoreList={handleRestoreList}
                restoringListId={restoringListId}
              />
            )}
          </>
        )}

        {/* Collections Tab Content */}
        {activeTab === 'collections' && canAccessCollections && (
          <>
            {collectionsLoading ? (
              <ListGridSkeleton count={3} />
            ) : collections.length === 0 ? (
              <div className="empty-state">
                <p>You haven't created any collections yet.</p>
                <p className="empty-state-subtext">Collections help you organize your lists into groups.</p>
                <button className="btn-primary" onClick={() => setCreateCollectionModalOpen(true)}>Create your first collection</button>
              </div>
            ) : (
              <div className={`collections-layout ${isReorderMode ? 'reorder-mode' : ''} ${expandedCollectionId ? 'has-expanded' : ''}`}>
                {/* Left panel: Collections list */}
                <div className="collections-panel">
                  <CollectionsDropZone
                    collections={filteredCollections}
                    isReorderMode={isReorderMode}
                    expandedCollectionId={expandedCollectionId}
                    getListsForCollection={getListsForCollection}
                    onCollectionDrop={handleCollectionDrop}
                    onCollectionListDrop={handleCollectionListDrop}
                    onCollectionClick={handleCollectionClick}
                    onCollectionEdit={handleCollectionEdit}
                    onListClick={handleListClick}
                  />
                </div>

                {/* Right panel: Selected collection's lists (desktop only) */}
                <div className="lists-panel">
                  {expandedCollectionId ? (() => {
                    const expandedCollection = collections.find(c => c.id === expandedCollectionId);
                    const collectionLists = expandedCollection ? getListsForCollection(expandedCollection.listIds) : [];

                    return (
                      <CollectionListsDropZone
                        collectionId={expandedCollectionId}
                        lists={collectionLists}
                        listIds={expandedCollection?.listIds || []}
                        isReorderMode={isReorderMode}
                        onDrop={handleCollectionListDrop}
                        onListClick={handleListClick}
                        onEditCollection={() => handleCollectionEdit(expandedCollection)}
                        className="lists-panel-grid"
                      />
                    );
                  })() : (
                    <div className="lists-panel-placeholder">
                      <p>Select a collection to view its lists</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Collection Modals */}
      <CreateCollectionModal
        isOpen={createCollectionModalOpen}
        onClose={() => setCreateCollectionModalOpen(false)}
      />

      <EditCollectionModal
        isOpen={editCollectionModalOpen}
        onClose={() => {
          setEditCollectionModalOpen(false);
          setSelectedCollection(null);
        }}
        collection={selectedCollection}
      />

      {/* Profile Drawer */}
      <ProfileDrawer
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
        onFollowersClick={() => setFollowersModalOpen(true)}
        onFriendsClick={() => setFriendsModalOpen(true)}
        listCount={lists.length}
        summaryStats={summaryStats}
      />

      {/* Followers Modal */}
      <UserListModal
        isOpen={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        title="Followers"
        users={followers}
        loading={loadingFollowers}
        emptyMessage="No followers yet"
        onUserClick={handleUserClick}
      />

      {/* Friends Modal */}
      <UserListModal
        isOpen={friendsModalOpen}
        onClose={() => setFriendsModalOpen(false)}
        title="Friends"
        users={friends}
        loading={loadingFriends}
        emptyMessage="No friends yet"
        onUserClick={handleUserClick}
      />

      {/* Collaborators Modal */}
      <CollaboratorsModal
        isOpen={collaboratorsModalOpen}
        onClose={() => {
          setCollaboratorsModalOpen(false);
          setSelectedCollabListId(null);
        }}
        listId={selectedCollabListId}
      />

      {/* Contextual Tooltip: Reorder Mode */}
      <ContextualTooltip
        id="reorder-lists"
        text="Drag lists to reorder. Pinned lists stay at top."
        anchorRef={reorderBtnRef}
        position="bottom"
        show={isReorderMode && canReorderLists}
        delay={500}
      />
    </div>
  );
}

export default MyLists;
